<!--
  Note: This component is only used by work-packages-new.vue, which is disabled everywhere.
-->
<template>
  <common-dialog
    :is-open="isOpen"
    :header-text="$t('workPackages.createNew')"
    :ok-btn-text="$t('actions.confirmCopy')"
    :ok-btn-disabled="!isValid"
    @onOk="save"
    @onCancel="cancel"
  >
    <template #body>
      <!-- Workpackage name -->
      <v-row no-gutters>
        <v-col cols="12">
          <v-text-field
            v-model.trim="workpackage.description"
            :label="$t('workPackages.enterWorkpackageName')"
            :rules="[valueRequired]"
          />
        </v-col>
      </v-row>

      <!-- Source workpackage selection -->
      <v-row no-gutters>
        <v-col cols="12">
          <div class="section-label">{{ $t('workPackages.sourceWorkpackage') }}</div>
          <v-autocomplete
            v-model="workpackage.workpackageId"
            :items="sourceWorkpackages"
            item-text="description"
            item-value="_id"
            :rules="[valueRequired]"
            @change="setAvailableHierarchiesAndStoreFormat($event)"
          />
        </v-col>
      </v-row>

      <!-- Completion and Go live dates -->
      <v-row>
        <v-col cols="6" class="pt-0 pb-0">
          <v-menu
            v-model="completionDatePicker"
            :close-on-content-click="false"
            :nudge-right="5"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="computedCompletionDate"
                :label="$t('workPackages.completionDate')"
                append-icon="event"
                readonly
                v-on="on"
                @click:append="completionDatePicker = true"
              />
            </template>
            <v-date-picker
              v-model="completionDate"
              :first-day-of-week="firstDayOfWeek"
              :locale="locale"
              @input="completionDatePicker = false"
            />
          </v-menu>
        </v-col>
        <v-col cols="6" class="pt-0 pb-0">
          <v-menu
            v-model="goLiveDatePicker"
            :close-on-content-click="false"
            :nudge-right="5"
            transition="scale-transition"
            offset-y
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="computedGoLiveDate"
                :label="$t('workPackages.goLiveDate')"
                append-icon="event"
                readonly
                v-on="on"
                @click:append="goLiveDatePicker = true"
              />
            </template>
            <v-date-picker
              v-model="goLiveDate"
              :first-day-of-week="firstDayOfWeek"
              :locale="locale"
              @input="onGoLiveDateInput()"
            />
          </v-menu>
        </v-col>
      </v-row>

      <!-- Reference date -->
      <feature-toggle toggle="enableReferenceDate">
        <v-row>
          <v-col cols="6" class="pt-0 pb-0">
            <v-menu
              v-model="referenceDatePicker"
              :close-on-content-click="false"
              :nudge-right="5"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="computedReferenceDate"
                  :label="$t('workPackages.referenceDate')"
                  append-icon="event"
                  readonly
                  v-on="on"
                  @click:append="referenceDatePicker = true"
                />
              </template>
              <v-date-picker
                v-model="referenceDate"
                :first-day-of-week="firstDayOfWeek"
                :locale="locale"
                :min="sixMonthsAgo"
                :max="todaysDate"
                @input="referenceDatePicker = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </feature-toggle>

      <!-- Pricing Groups Selection (when hierarchyLevel is pricingGroupLevel) -->
      <v-row
        v-if="creationHierarchyLevel === pricingGroupLevel && availablePricingGroups.length > 0"
        no-gutters
      >
        <v-col cols="12">
          <div class="section-label">{{ $t('pricing.pricingGroup') }}</div>
          <v-autocomplete
            v-model="workpackage.hierarchyIds"
            :items="availablePricingGroups"
            item-text="levelEntryDescription"
            item-value="levelEntryKey"
            multiple
            chips
            small-chips
            deletable-chips
          />
        </v-col>
      </v-row>

      <!-- Categories Selection (when hierarchyLevel is categoryLevel) -->
      <v-row
        v-if="creationHierarchyLevel === categoryLevel && availableCategories.length > 0"
        no-gutters
      >
        <v-col cols="12" class="category-selection">
          <div class="section-label">{{ $t('workPackages.categories') }}</div>
          <v-expansion-panels>
            <v-expansion-panel v-for="category in availableCategories" :key="category._id">
              <v-expansion-panel-header>
                <div class="d-flex justify-space-between align-center w-100">
                  <div class="category-description">{{ category.levelEntryDescription }}</div>
                  <v-checkbox
                    :input-value="isUnitAllSelected(category)"
                    :indeterminate="isUnitIndeterminate(category)"
                    :disabled="getCategoriesForUnit(category).length === 0"
                    dense
                    hide-details
                    class="ma-0 pa-0"
                    @click.stop
                    @change="toggleAllCategoriesForUnit(category._id, $event)"
                  />
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="category-items">
                  <div
                    v-for="subcategory in getCategoriesForUnit(category)"
                    :key="subcategory.levelEntryKey"
                    class="d-flex justify-space-between align-center py-1"
                  >
                    <div class="category-description">
                      {{ subcategory.levelEntryDescription }}
                    </div>
                    <v-checkbox
                      v-model="selectedHierarchies[category._id]"
                      :value="subcategory.levelEntryKey"
                      dense
                      hide-details
                      class="ma-0 pa-0"
                    />
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Workpackage Period Type (weekly / aoPeriod) -->
      <feature-toggle toggle="enableWorkpackagePeriodTypes">
        <v-row no-gutters>
          <v-col cols="6">
            <v-select
              v-model="workpackagePeriodType"
              :items="workpackagePeriodTypeItems"
              :label="`${$t('workPackages.workpackagePeriodType')} *`"
              :rules="[valueRequired]"
              item-text="text"
              item-value="value"
            />
          </v-col>
        </v-row>
      </feature-toggle>

      <!-- AO Extracts Selection -->
      <feature-toggle toggle="enableAOExtracts">
        <v-row no-gutters>
          <v-col cols="12">
            <div class="section-label">{{ $t('workPackages.aoExtracts') }}</div>
            <v-autocomplete
              v-model="selectedAOExtracts"
              :items="aoExtractsOptions"
              multiple
              chips
              small-chips
              deletable-chips
              dense
              item-text="displayName"
              item-value="auditCorrelationId"
              :rules="[aoExtractsRules]"
            />

            <feature-toggle toggle="usePeriodIdsInAOExtracts">
              <template v-if="selectedAOExtracts.length > 0">
                <div class="mb-2">{{ $t('workPackages.associatedPeriodIds') }}</div>
                <div>{{ getAssociatedPeriodIdsText }}</div>
              </template>
            </feature-toggle>
          </v-col>
        </v-row>
      </feature-toggle>

      <optimise-prices-toggle />
      <refresh-on-create-toggle />
    </template>
  </common-dialog>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import { get, isEmpty, sortBy, size } from 'lodash';
import moment from 'moment';
import { yearMonthDayFormat, utcFormat } from '@enums/date-formats';
import { pricingGroupLevel, categoryLevel } from '@enums/hierarchy';
import featureFlagsMixin from '../../../mixins/featureFlags';
import {
  enableAOExtracts,
  workpackageCreationHierarchyLevel,
  createWorkpackageFromCopy,
  enableWorkpackagePeriodTypes,
} from '@enums/feature-flags';
import workpackagePeriodTypes from '@enums/workpackage-period-types';

const todaysDate = moment().format('YYYY-MM-DD');
const sixMonthsAgo = moment()
  .subtract(6, 'months')
  .format('YYYY-MM-DD');
const defaultWorkpackagePeriodType = workpackagePeriodTypes.aoPeriod;

export default {
  name: 'CreateWorkpackageDialog',

  mixins: [featureFlagsMixin],

  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      completionDate: new Date().toISOString().substr(0, 10),
      goLiveDate: new Date().toISOString().substr(0, 10),
      referenceDate: null,
      completionDatePicker: false,
      goLiveDatePicker: false,
      referenceDatePicker: false,
      todaysDate,
      sixMonthsAgo,
      workpackage: {
        description: '',
        workpackageId: null,
        storeFormat: '',
        hierarchyIds: [],
      },
      storeFormat: '',
      availablePricingGroups: [],
      availableCategories: [],
      selectedHierarchies: {},
      selectedAOExtracts: [],
      workpackagePeriodType: defaultWorkpackagePeriodType,
      pricingGroupLevel,
      categoryLevel,
    };
  },

  computed: {
    ...mapState('clientConfig', ['i18nconfig', 'toggleLogic']),
    ...mapState('context', ['profile']),
    ...mapState('workpackages', ['creating']),
    ...mapGetters('workpackages', [
      'masterWorkpackages',
      'activeWorkpackages',
      'archivedWorkpackages',
      'getAvailableAOExtracts',
      'getAOExtractsByKey',
      'getAssociatedPeriodIds',
      'isAOExtractsValid',
    ]),
    ...mapGetters('hierarchy', ['getHierarchyForWorkpackage']),

    loading() {
      return this.creating;
    },

    firstDayOfWeek() {
      return get(this.i18nconfig, 'firstDayOfTheWeek', 0);
    },

    locale() {
      return get(this.i18nconfig, 'fallbackLocale', 'en');
    },

    sourceWorkpackages() {
      if (this.isFeatureFlagEnabled(createWorkpackageFromCopy)) {
        return [
          ...this.masterWorkpackages,
          ...this.activeWorkpackages,
          ...this.archivedWorkpackages,
        ];
      }
      return this.masterWorkpackages;
    },

    creationHierarchyLevel() {
      return this.toggleLogic[workpackageCreationHierarchyLevel] || pricingGroupLevel;
    },

    computedCompletionDate() {
      return this.formatLocalisedDate(this.completionDate);
    },

    computedGoLiveDate() {
      return this.formatLocalisedDate(this.goLiveDate);
    },

    computedReferenceDate() {
      return this.referenceDate ? this.formatLocalisedDate(this.referenceDate) : this.referenceDate;
    },

    aoExtractsOptions() {
      return this.getAvailableAOExtracts(this.goLiveDate);
    },

    workpackagePeriodTypeItems() {
      return [
        { text: this.$t('workPackages.periodTypes.weekly'), value: workpackagePeriodTypes.weekly },
        {
          text: this.$t('workPackages.periodTypes.aoPeriod'),
          value: workpackagePeriodTypes.aoPeriod,
        },
      ];
    },

    getAssociatedPeriodIdsText() {
      if (!this.selectedAOExtracts || !this.selectedAOExtracts.length) return '';
      return this.getAssociatedPeriodIds(this.selectedAOExtracts);
    },

    isValid() {
      const hasRequiredFields =
        !isEmpty(this.workpackage.description.trim()) && !isEmpty(this.workpackage.workpackageId);

      const hasHierarchyIds = !isEmpty(this.getEffectiveHierarchyIds());

      const hasValidWorkpackagePeriodType =
        !this.isFeatureFlagEnabled(enableWorkpackagePeriodTypes) ||
        !isEmpty(this.workpackagePeriodType);

      const hasValidAoExtracts =
        !this.isFeatureFlagEnabled(enableAOExtracts) ||
        this.isAOExtractsValid(this.selectedAOExtracts);

      return (
        hasRequiredFields && hasHierarchyIds && hasValidAoExtracts && hasValidWorkpackagePeriodType
      );
    },
  },

  watch: {
    isOpen(value) {
      if (!value) {
        this.resetForm();
      }
    },
  },

  methods: {
    ...mapActions('workpackages', ['createWorkpackage']),
    ...mapMutations('workpackages', ['defaultCreateWorkpackageOptions']),

    valueRequired(v) {
      return !!v || v === 0 || this.$t('settings.parameters.errors.fieldInvalid');
    },

    aoExtractsRules() {
      return (
        this.isAOExtractsValid(this.selectedAOExtracts) ||
        this.$t('validationErrors.periodIdsDatesDoNotMatch')
      );
    },

    setAvailableHierarchiesAndStoreFormat(selectedSourceWorkpackageId) {
      const selectedSourceWorkpackage = this.sourceWorkpackages.find(
        wp => wp._id === selectedSourceWorkpackageId
      );

      if (!selectedSourceWorkpackage) return;

      this.workpackage.storeFormat = selectedSourceWorkpackage.storeFormat;
      this.storeFormat = selectedSourceWorkpackage.storeFormat;
      this.workpackage.hierarchyIds = [];

      if (this.creationHierarchyLevel === categoryLevel) {
        this.setAvailableCategories(selectedSourceWorkpackage);
      } else if (this.creationHierarchyLevel === pricingGroupLevel) {
        this.setAvailablePricingGroups(selectedSourceWorkpackage);
      }
    },

    setAvailablePricingGroups(workpackage) {
      this.availablePricingGroups = this.getHierarchyForWorkpackage(
        { level: pricingGroupLevel },
        workpackage._id
      );
      this.workpackage.hierarchyLevel = this.creationHierarchyLevel;
    },

    setAvailableCategories(workpackage) {
      const retailUnits = get(workpackage, 'units', []).filter(unit => unit.wholesale === false);
      this.availableCategories = sortBy(retailUnits, 'levelEntryDescription');

      // Initialize selected categories for each unit
      this.selectedHierarchies = {};
      this.availableCategories.forEach(unit => {
        this.selectedHierarchies[unit._id] = [];
      });

      this.workpackage.hierarchyLevel = this.creationHierarchyLevel;
    },

    getCategoriesForUnit(unit) {
      return this.getHierarchyForWorkpackage(
        {
          level: unit.level + 1,
          parentId: unit.levelEntryKey,
        },
        this.workpackage.workpackageId
      );
    },

    toggleAllCategoriesForUnit(unitId, value) {
      const unit = (this.availableCategories || []).find(u => u._id === unitId);
      if (!unit) return;

      const categories = this.getCategoriesForUnit(unit) || [];
      const keys = categories.map(c => c.levelEntryKey);

      this.selectedHierarchies = {
        ...this.selectedHierarchies,
        [unitId]: value ? keys : [],
      };
    },

    getEffectiveHierarchyIds() {
      if (this.creationHierarchyLevel === pricingGroupLevel) {
        return this.workpackage.hierarchyIds;
      }
      if (this.creationHierarchyLevel === categoryLevel) {
        const allSelectedIds = [];
        Object.values(this.selectedHierarchies).forEach(categoryIds => {
          allSelectedIds.push(...categoryIds);
        });
        return allSelectedIds;
      }
      return [];
    },

    onGoLiveDateInput() {
      this.goLiveDatePicker = false;
      if (this.isFeatureFlagEnabled(enableAOExtracts)) {
        // Clear selected AO extracts when go-live date changes
        this.selectedAOExtracts = [];
      }
    },

    async save() {
      // Create a new workpackage object for submission
      const newWorkpackage = {
        description: this.workpackage.description,
        storeFormat: this.workpackage.storeFormat,
        workpackageId: this.workpackage.workpackageId,
        owner: this.profile.username,
        hierarchyLevel: this.creationHierarchyLevel,
        hierarchyIds: this.getEffectiveHierarchyIds(),
        completionDate: moment(this.completionDate).format(utcFormat),
        goLiveDate: moment(this.goLiveDate).format(yearMonthDayFormat),
      };

      if (this.referenceDate) {
        newWorkpackage.referenceDate = moment(this.referenceDate).format(yearMonthDayFormat);
      }

      // Add AO extracts if enabled
      if (this.isFeatureFlagEnabled(enableAOExtracts) && this.selectedAOExtracts.length > 0) {
        newWorkpackage.aoExtracts = this.selectedAOExtracts.map(id => this.getAOExtractsByKey[id]);
      }

      // Add workpackage period type if enabled
      if (this.isFeatureFlagEnabled(enableWorkpackagePeriodTypes)) {
        newWorkpackage.workpackagePeriodType = this.workpackagePeriodType;
      }

      try {
        await this.createWorkpackage(newWorkpackage);
        this.$emit('created');
        this.$emit('update:isOpen', false);
      } catch (error) {
        console.error('Failed to create workpackage:', error);
      }
    },

    cancel() {
      this.$emit('update:isOpen', false);
      this.$emit('cancel');
    },

    isUnitAllSelected(unit) {
      const total = size(this.getCategoriesForUnit(unit));
      const selected = size(get(this.selectedHierarchies, [unit._id], []));
      return total > 0 && selected === total;
    },

    isUnitIndeterminate(unit) {
      const total = size(this.getCategoriesForUnit(unit));
      const selected = size(get(this.selectedHierarchies, [unit._id], []));
      return selected > 0 && selected < total;
    },

    resetForm() {
      this.completionDate = new Date().toISOString().substr(0, 10);
      this.goLiveDate = new Date().toISOString().substr(0, 10);
      this.referenceDate = null;
      this.workpackage = {
        description: '',
        workpackageId: null,
        storeFormat: '',
        hierarchyIds: [],
      };
      this.storeFormat = '';
      this.availablePricingGroups = [];
      this.availableCategories = [];
      this.selectedHierarchies = {};
      this.selectedAOExtracts = [];
      this.workpackagePeriodType = defaultWorkpackagePeriodType;
      this.defaultCreateWorkpackageOptions();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.section-label {
  font-size: 1.4rem;
  font-weight: 500;
  margin-top: 16px;
  margin-bottom: 8px;
}

.category-items {
  max-height: 200px;
  overflow-y: auto;
  padding: 2px 0;
}

.category-selection {
  .v-expansion-panel {
    background: transparent;
    margin-top: 0;
  }

  .category-description {
    font-size: 1.2rem;
  }

  .v-expansion-panel-content {
    padding-left: 2rem;
  }

  .v-expansion-panel--active > .v-expansion-panel-header {
    min-height: 48px;
  }
}

.w-100 {
  width: 100%;
}
</style>
